import {
	KeyboardEvent,
	PropsWithChildren,
	ReactElement,
	Ref,
	forwardRef,
	useState,
	useContext,
	useEffect
} from "react";
import { ActionListBoxContext } from "../ActionListBox/ActionListBox";
import { ActionListHeader } from "./ActionListHeader";
import { isNotNil } from "client/Misc/utils";

export type ActionListCollapsibleSectionProps = {
	header: string;
	suffix?: string;
	trackingId: string;
	isExpandedByDefault?: boolean;
	onKeyUp?: (args: KeyboardEvent<HTMLLIElement>) => void;
	onKeyDown?: (args: KeyboardEvent<HTMLLIElement>) => void;
	tabIndex?: number;
	indentionLevel?: number;
	hasLine?: boolean;
	role?: "option" | "group";
} & ({
	isCollapsed: boolean;
	onToggle: () => void;
} | {
	isCollapsed?: undefined;
	onToggle?: undefined;
});

export const ActionListCollapsibleSection = forwardRef<HTMLLIElement, PropsWithChildren<ActionListCollapsibleSectionProps>>(
	(props: PropsWithChildren<ActionListCollapsibleSectionProps>, ref: Ref<HTMLLIElement>): ReactElement => {
		const {
			header,
			suffix,
			trackingId,
			isExpandedByDefault = false,
			children,
			tabIndex,
			indentionLevel,
			hasLine = false,
			isCollapsed: isCollapsedFromProps,
			role,
			onKeyUp,
			onKeyDown,
			onToggle
		} = props;

		const [isCollapsed, setIsCollapsed] = useState<boolean>(!isExpandedByDefault);

		const isCollapsedToRender: boolean = isNotNil(isCollapsedFromProps)
			? isCollapsedFromProps
			: isCollapsed;

		const {
			registerCollapsibleSection
		} = useContext(ActionListBoxContext);

		useEffect(
			(): (() => void) | void => registerCollapsibleSection?.(),
			[registerCollapsibleSection]
		);

		return (
			<>
				<ActionListHeader
					text={header}
					suffix={suffix}
					rightIcon={isCollapsedToRender ? "chevron-down" : "chevron-up"}
					trackingId={trackingId}
					onClick={onToggle
						? onToggle
						: () => setIsCollapsed((x) => !x)
					}
					onKeyUp={onKeyUp}
					onKeyDown={onKeyDown}
					tabIndex={tabIndex}
					ref={ref}
					indentionLevel={indentionLevel}
					hasLine={hasLine}
					role={role}
				/>

				{!isCollapsedToRender && (
					<>
						{children}
					</>
				)}
			</>
		);
	}
);
ActionListCollapsibleSection.displayName = "ActionListCollapsibleSection";
